import { ResourceClient, type HttpClient } from "@dl/web-core-js/api";
import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import {
  Badge,
  Button,
  Dialog,
  Field,
  MultiSelect,
  Select,
  Switch,
  useConfirm,
} from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Lock, PauseCircle, PlayCircle, RefreshCw, Unlock } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  imageUrl,
  PRODUCT_TYPES,
  units,
  type LazyProductDTO,
  type ProductImageDTO,
  type ProductKpiSalesDTO,
  type ProductStatus,
} from "../../api/product";
import { SETTINGS_PATHS, fetchSingleRow, settingsClient, settingsKeys, type ProductSettingsDTO } from "../../api/settings";
import { NexpimView } from "../../views";
import { isoDate } from "../../api/reporting";

/* ------------------------------------------------------- COGS config --- */

/** One computed COGS run for a product over a period. */
interface ProductCogsDTO {
  id: number;
  product_id: number;
  produced: number;
  production_labor_cogs: number;
  production_overhead_cogs: number;
  assembly_labor_cogs: number;
  assembly_overhead_cogs: number;
  material_cogs: number;
  cogs: number;
  date_from: string;
  date_to: string;
  is_active: boolean;
  date_activated?: string | null;
}

interface LazyProductCogsDTO {
  id: number;
  status: ProductStatus;
  metadata?: { sku?: string } | null;
  description_en?: { product_name?: string | null } | null;
  category?: { name?: string | null } | null;
  pricing?: { cogs?: number | null; msrp?: number | null } | null;
  kpi_sales?: ProductKpiSalesDTO | null;
  cogs: ProductCogsDTO[];
  image?: ProductImageDTO | null;
}

class LazyProductCogsClient extends ResourceClient<LazyProductCogsDTO> {
  constructor(http: HttpClient) {
    super(http, "/product/lazy/cogs");
  }

  byType(type: "SINGLE_ITEM" | "COMPONENT"): Promise<LazyProductCogsDTO[]> {
    return this.http.get<LazyProductCogsDTO[]>(`/product/lazy/cogs/type/${type}`);
  }
}

const COGS_PERIODS = ["TRIMESTER", "SEMESTER", "YEAR", "YEAR_ROLLING"] as const;
type CogsPeriod = (typeof COGS_PERIODS)[number];

interface PeriodColumn {
  key: string;
  label: string;
  from: string;
  to: string;
}

const pad = (value: number) => String(value).padStart(2, "0");
const lastDayOf = (year: number, monthIndex: number) => new Date(year, monthIndex + 1, 0).getDate();

/**
 * Expands the chosen years × period shapes into concrete date ranges.
 *
 * YEAR_ROLLING is the twelve months ending today rather than a calendar year —
 * it is what the costing team uses mid-year, when no calendar period is
 * complete enough to be representative.
 */
function periodColumns(years: number[], periods: CogsPeriod[], today = new Date()): PeriodColumn[] {
  const result: PeriodColumn[] = [];
  for (const year of [...years].sort()) {
    for (const period of periods) {
      switch (period) {
        case "TRIMESTER":
          for (let quarter = 1; quarter <= 4; quarter += 1) {
            const startMonth = (quarter - 1) * 3;
            result.push({
              key: `q${quarter}-${year}`,
              label: `Q${quarter} ${year}`,
              from: `${year}-${pad(startMonth + 1)}-01`,
              to: `${year}-${pad(startMonth + 3)}-${pad(lastDayOf(year, startMonth + 2))}`,
            });
          }
          break;
        case "SEMESTER":
          for (let half = 1; half <= 2; half += 1) {
            const startMonth = (half - 1) * 6;
            result.push({
              key: `s${half}-${year}`,
              label: `S${half} ${year}`,
              from: `${year}-${pad(startMonth + 1)}-01`,
              to: `${year}-${pad(startMonth + 6)}-${pad(lastDayOf(year, startMonth + 5))}`,
            });
          }
          break;
        case "YEAR":
          result.push({
            key: `y-${year}`,
            label: String(year),
            from: `${year}-01-01`,
            to: `${year}-12-31`,
          });
          break;
        case "YEAR_ROLLING": {
          const end = new Date(today);
          end.setFullYear(end.getFullYear() - (today.getFullYear() - year));
          const start = new Date(end);
          start.setFullYear(start.getFullYear() - 1);
          start.setDate(start.getDate() + 1);
          result.push({
            key: `r-${year}`,
            label: `Rolling ${year}`,
            from: `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}`,
            to: `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`,
          });
          break;
        }
      }
    }
  }
  return result;
}

/**
 * The COGS matrix: every buildable product against the costing periods, with
 * the run that is currently priced-from highlighted.
 *
 * Two things make this screen dangerous, and both are guarded the way the
 * Vaadin grid guarded them. Recomputing a period rewrites every product's cost
 * for it, and activating one makes it the number the whole catalogue prices
 * against — so the grid opens read-only behind an explicit unlock, and both
 * actions confirm.
 */
export function CogsConfigPage() {
  const { t } = useTranslation();
  const { http, hasView } = useAuth();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const client = useMemo(() => new LazyProductCogsClient(http), [http]);
  const cogsClient = useMemo(() => new ProductCogsClient(http), [http]);
  const allowed = hasView(NexpimView.CONFIG_PRODUCT_COGS);

  const currentYear = new Date().getFullYear();
  const [unlocked, setUnlocked] = useState(false);
  const [years, setYears] = useState<string[]>([String(currentYear)]);
  const [periods, setPeriods] = useState<string[]>(["TRIMESTER"]);
  const [syncing, setSyncing] = useState<PeriodColumn | null>(null);
  const [simulatedSku, setSimulatedSku] = useState("");
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["config", "lazy-cogs"],
    enabled: allowed,
    queryFn: async () => {
      // Bundles have no cost of their own — they inherit their items'.
      const [singles, components] = await Promise.all([
        client.byType("SINGLE_ITEM"),
        client.byType("COMPONENT"),
      ]);
      return [...singles, ...components].sort(
        (a, b) => units(b.kpi_sales, "twelve_weeks") - units(a.kpi_sales, "twelve_weeks"),
      );
    },
    staleTime: 5 * 60_000,
  });

  const rows = data ?? [];
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["config", "lazy-cogs"] });

  const columnsForPeriods = useMemo(
    () => periodColumns(years.map(Number), periods as CogsPeriod[]),
    [periods, years],
  );

  const guard = async (title: string, message: string): Promise<boolean> => {
    if (!unlocked) {
      toast.error(t("cogs.locked", "Unlock editing first."));
      return false;
    }
    return confirm({ title, message });
  };

  /** Activating one run deactivates the rest — a product has exactly one cost. */
  const activate = async (product: LazyProductCogsDTO, column: PeriodColumn) => {
    const target = product.cogs.find(
      (entry) => entry.date_from === column.from && entry.date_to === column.to,
    );
    if (!target) return;
    const ok = await guard(
      t("dialog.confirm.activate-cogs.title", "Activate this COGS?"),
      `${product.metadata?.sku ?? ""} · ${column.from} - ${column.to}`,
    );
    if (!ok) return;
    try {
      await client.update({
        ...product,
        cogs: product.cogs.map((entry) => ({
          ...entry,
          is_active: entry.id === target.id,
          date_activated:
            entry.id === target.id ? isoDate(new Date()) : entry.date_activated,
        })),
      });
      await refresh();
      toast.success(t("notification.activate-cogs.success", "COGS activated"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("notification.cogs-activate.error", "Failed"),
      );
    }
  };

  /** Activates one period for every product in the grid at once. */
  const activateAll = async (column: PeriodColumn) => {
    const ok = await guard(
      t("dialog.confirm.activate-cogs.title", "Activate this COGS?"),
      `${t("cogs.activate-all", "Every product")} · ${column.from} - ${column.to}`,
    );
    if (!ok) return;
    setBusy(true);
    try {
      for (const product of rows) {
        const target = product.cogs.find(
          (entry) => entry.date_from === column.from && entry.date_to === column.to,
        );
        if (!target) continue;
        await client.update({
          ...product,
          cogs: product.cogs.map((entry) => ({
            ...entry,
            is_active: entry.id === target.id,
            date_activated:
              entry.id === target.id ? isoDate(new Date()) : entry.date_activated,
          })),
        });
      }
      await refresh();
      toast.success(t("notification.activate-cogs.success", "COGS activated"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.generic", "Failed"));
    } finally {
      setBusy(false);
    }
  };

  const runSync = async () => {
    if (!syncing) return;
    setBusy(true);
    try {
      // The recompute divides period expenses across what was produced, so an
      // expense row for the period has to exist first.
      const expenses = await cogsClient.expensesForPeriods(syncing.from, syncing.to);
      if (expenses.length === 0) {
        toast.error(t("notification.sync-cogs.no-expense", "No COGS expense covers this period."));
        return;
      }
      // A simulated SKU lets the costing team ask "what if we had built 20 of
      // these" without writing a production record.
      const simulated = simulatedSku ? { [simulatedSku]: 20 } : {};
      await cogsClient.calculate(syncing.from, syncing.to, simulated);
      toast.success(t("notification.sync-cogs.started.title", "COGS recomputed"));
      setSyncing(null);
      setSimulatedSku("");
      await refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.generic", "Failed"));
    } finally {
      setBusy(false);
    }
  };

  const cols = useMemo(() => {
    const c = defineColumns<LazyProductCogsDTO>();
    const base = [
      c.custom(
        "thumbnail",
        () => "",
        (row) => {
          const url = imageUrl(row.image);
          return url ? (
            <img
              src={url}
              alt=""
              loading="lazy"
              onError={(event) => {
                event.currentTarget.style.visibility = "hidden";
              }}
              className="size-12 rounded object-cover"
            />
          ) : (
            <span className="block size-12 rounded bg-surface-sunk" />
          );
        },
        { headerKey: "column.thumbnail", filter: "none", enableSorting: false, size: 64 },
      ),
      c.text("sku", (row) => row.metadata?.sku ?? "", { headerKey: "column.sku", priority: 1 }),
      c.custom(
        "status",
        (row) => row.status,
        (row) => (
          <Badge variant={COGS_STATUS_VARIANT[row.status] ?? "neutral"} size="sm">
            {row.status}
          </Badge>
        ),
        { headerKey: "column.status", filter: "select", priority: 3 },
      ),
      c.text("name", (row) => row.description_en?.product_name ?? "", {
        headerKey: "column.name",
        priority: 2,
      }),
      c.number("sales_12w", (row) => units(row.kpi_sales, "twelve_weeks"), {
        headerKey: "column.sales.12-weeks",
        filter: "range",
        priority: 3,
      }),
      c.custom(
        "active_cogs",
        (row) => row.cogs.find((entry) => entry.is_active)?.cogs ?? 0,
        (row) => {
          const active = row.cogs.find((entry) => entry.is_active);
          if (!active) return <span className="text-ink-faint">no active cogs</span>;
          return (
            <span className="flex flex-col leading-tight">
              <span className="font-semibold text-ok tabular-nums">{active.cogs.toFixed(2)}</span>
              <span className="text-[11px] text-ink-muted">{`${active.date_from} → ${active.date_to}`}</span>
            </span>
          );
        },
        { headerKey: "column.active-cogs", filter: "range", align: "right", priority: 3 },
      ),
    ];

    const periodCols = columnsForPeriods.map((column) =>
      c.custom(
        column.key,
        (row) =>
          row.cogs.find((entry) => entry.date_from === column.from && entry.date_to === column.to)
            ?.cogs ?? 0,
        (row) => {
          const entry = row.cogs.find(
            (item) => item.date_from === column.from && item.date_to === column.to,
          );
          if (!entry) return <span className="text-ink-faint"></span>;
          if (entry.is_active) {
            return <span className="font-bold text-ok tabular-nums">{entry.cogs.toFixed(2)}</span>;
          }
          return (
            <button
              type="button"
              title={t("cogs.activate", "Make this the active COGS")}
              onClick={(event) => {
                event.stopPropagation();
                void activate(row, column);
              }}
              className="tabular-nums text-ink-muted underline decoration-dotted hover:text-accent"
            >
              {entry.cogs.toFixed(2)}
            </button>
          );
        },
        { header: column.label, filter: "range", align: "right" },
      ),
    );

    return [...base, ...periodCols];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnsForPeriods, rows, unlocked, t]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.config-cogs", "COGS")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t("navigation.item.config-cogs", "COGS")}
        subtitle={t("config.cogs.subtitle", "Computed cost per product.")}
        badges={
          <Badge variant={unlocked ? "warning" : "neutral"} size="sm">
            {unlocked ? t("cogs.unlocked", "Editing unlocked") : t("cogs.locked-badge", "Read only")}
          </Badge>
        }
      />

      <DataTable
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={isLoading}
        initialSorting={[{ id: "sales_12w", desc: true }]}
        actions={[
          {
            label: unlocked ? t("cogs.lock", "Lock editing") : t("cogs.unlock", "Unlock editing"),
            icon: unlocked ? <Unlock className="size-4" /> : <Lock className="size-4" />,
            variant: unlocked ? "success" : "secondary",
            write: true,
            onClick: () => setUnlocked((value) => !value),
          },
        ]}
        toolbarExtra={
          <div className="ml-auto flex items-center gap-2">
            <div className="w-40">
              <MultiSelect
                options={[0, 1, 2, 3].map((offset) => ({
                  value: String(currentYear - offset),
                  label: String(currentYear - offset),
                }))}
                value={years}
                onChange={setYears}
                placeholder={t("cogs.years", "Years")}
              />
            </div>
            <div className="w-44">
              <MultiSelect
                options={COGS_PERIODS.map((value) => ({ value, label: value.replace("_", " ") }))}
                value={periods}
                onChange={setPeriods}
                placeholder={t("cogs.periods", "Periods")}
              />
            </div>
          </div>
        }
        emptyMessage={t("table.empty", "Nothing to show")}
      />

      {/* Per-period controls sit outside the grid header: a header cell can't
          hold a button in this table, and burying them would make two
          destructive actions hard to find. */}
      {columnsForPeriods.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-t border-line bg-surface px-3 py-2">
          <span className="text-[12px] text-ink-muted">{t("cogs.period-actions", "Period actions:")}</span>
          {columnsForPeriods.map((column) => (
            <span key={column.key} className="flex items-center gap-1 rounded border border-line px-2 py-1">
              <span className="text-[12px] font-medium text-ink">{column.label}</span>
              <Button
                size="sm"
                variant="ghost"
                className="size-6 p-0"
                title={t("cogs.sync", "Recompute this period")}
                disabled={busy}
                icon={<RefreshCw className="size-3.5" />}
                onClick={() => {
                  if (!unlocked) {
                    toast.error(t("cogs.locked", "Unlock editing first."));
                    return;
                  }
                  setSyncing(column);
                }}
              />
              <Button
                size="sm"
                variant="ghost"
                className="size-6 p-0"
                title={t("cogs.activate-all", "Activate for every product")}
                disabled={busy}
                icon={<CheckCircle2 className="size-3.5" />}
                onClick={() => void activateAll(column)}
              />
            </span>
          ))}
        </div>
      )}

      <Dialog
        open={syncing !== null}
        onClose={() => setSyncing(null)}
        title={t("dialog.sync-cogs.title", "Recompute COGS")}
        description={syncing ? `${syncing.from} - ${syncing.to}` : ""}
        size="sm"
        actions={
          <>
            <Button variant="secondary" onClick={() => setSyncing(null)}>
              {t("button.cancel", "Cancel")}
            </Button>
            <Button variant="primary" loading={busy} onClick={() => void runSync()}>
              {t("dialog.confirm", "Confirm")}
            </Button>
          </>
        }
      >
        <Field
          label={t("form.field.simulated-product", "Simulated product")}
          hint={t(
            "form.field.simulated-product.helper",
            "Adds 20 simulated units of this SKU to the run, to see what building it would cost.",
          )}
        >
          <Select
            value={simulatedSku}
            placeholder={t("form.field.select", "Select")}
            options={rows
              .map((row) => row.metadata?.sku ?? "")
              .filter(Boolean)
              .map((sku) => ({ value: sku, label: sku }))}
            onChange={(event) => setSimulatedSku(event.target.value)}
          />
        </Field>
      </Dialog>
    </>
  );
}

const COGS_STATUS_VARIANT: Record<ProductStatus, "success" | "neutral" | "error"> = {
  AVAILABLE: "success",
  PENDING_LAUNCH: "neutral",
  PENDING_DISCONTINUED: "error",
  DISCONTINUED: "error",
};

/** The COGS write endpoints: recompute a period, and read its expense rows. */
class ProductCogsClient {
  constructor(private readonly http: HttpClient) {}

  async calculate(from: string, to: string, simulated: Record<string, number>): Promise<void> {
    await this.http.post<void>("/product/cogs/calculate", simulated, {
      responseType: "void",
      query: { date_from: from, date_to: to },
    });
  }

  expensesForPeriods(from: string, to: string): Promise<Array<{ id: number }>> {
    return this.http.get<Array<{ id: number }>>("/cogs/expense/periods", {
      query: { date_from: from, date_to: to },
    });
  }
}

/* ----------------------------------- Automatic product discontinuation --- */

type ApdStatus = "PENDING_DISCONTINUATION" | "ON_HOLD" | "DISCONTINUED";

interface ApdDTO {
  id: number;
  product_id: number;
  product?: LazyProductDTO | null;
  status: ApdStatus;
  flagged_at?: string | null;
  discontinue_at?: string | null;
  discontinued_at?: string | null;
  reason?: string | null;
  note?: string | null;
}

const APD_VARIANT: Record<ApdStatus, "warning" | "info" | "error"> = {
  PENDING_DISCONTINUATION: "warning",
  ON_HOLD: "info",
  DISCONTINUED: "error",
};

/**
 * The slow-mover watchlist. A backend job flags products for discontinuation;
 * the operator's decision here is binary — let it happen, or put the product on
 * hold. Both act on the selection, as the Vaadin buttons did.
 */
export function AutomaticDiscontinuationPage() {
  const { t } = useTranslation();
  const { http, hasView } = useAuth();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const client = useMemo(
    () => new ResourceClient<ApdDTO>(http, "/automatic-product-discontinuation"),
    [http],
  );
  const allowed = hasView(NexpimView.CONFIG_AUTOMATIC_PRODUCT_DISCONTINUATION);
  const [selected, setSelected] = useState<ApdDTO[]>([]);

  // The switch of the Monday job, which is product_settings' flag: the same
  // row, and the same cache entry, as the Settings page. Off, nothing is
  // flagged and nothing changes status; the list below keeps its dates, so a
  // long pause followed by "on" moves every expired product on the next run —
  // the hint says so.
  const settingsQuery = useQuery({
    queryKey: settingsKeys.product,
    queryFn: () => fetchSingleRow<ProductSettingsDTO>(http, SETTINGS_PATHS.product),
    enabled: allowed,
  });
  const setEnabled = useMutation({
    mutationFn: async (enabled: boolean) => {
      const current = await fetchSingleRow<ProductSettingsDTO>(http, SETTINGS_PATHS.product);
      const next = { ...current, is_automatic_discontinuation_enabled: enabled };
      await settingsClient<ProductSettingsDTO>(http, SETTINGS_PATHS.product).update(next);
      return next;
    },
    onSuccess: (result) => {
      queryClient.setQueryData(settingsKeys.product, result);
      toast.success(
        result.is_automatic_discontinuation_enabled
          ? t("apd.enabled.on", "Automatic discontinuation is on: the job runs Mondays at 14:00.")
          : t("apd.enabled.off", "Automatic discontinuation is off: nothing will be flagged or discontinued."),
      );
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : t("error.generic", "Something went wrong. Please try again."));
    },
  });
  const enabled = settingsQuery.data?.is_automatic_discontinuation_enabled ?? false;

  const { data, isLoading } = useQuery({
    queryKey: ["config", "apd"],
    queryFn: () => client.findAll(),
    enabled: allowed,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["config", "apd"] });

  const setStatus = async (status: ApdStatus, title: string) => {
    // Discontinued rows are history; only actionable rows change.
    const rows = selected.filter((row) => row.status !== "DISCONTINUED");
    if (rows.length === 0) return;

    const confirmed = await confirm({
      title,
      message: t("apd.count", "{{count}} product(s)", { count: rows.length }),
    });
    if (!confirmed) return;

    try {
      for (const row of rows) {
        await client.update({ ...row, status });
      }
      await refresh();
      toast.success(t("notification.saved", "Saved"));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("error.generic", "Failed"));
    }
  };

  const cols = useMemo(() => {
    const c = defineColumns<ApdDTO>();
    return [
      c.custom(
        "thumbnail",
        () => "",
        (row) => {
          const url = imageUrl(row.product?.image);
          return url ? (
            <img
              src={url}
              alt=""
              loading="lazy"
              onError={(event) => {
                event.currentTarget.style.visibility = "hidden";
              }}
              className="size-12 rounded object-cover"
            />
          ) : (
            <span className="block size-12 rounded bg-surface-sunk" />
          );
        },
        { headerKey: "column.thumbnail", filter: "none", enableSorting: false, size: 64 },
      ),
      c.text("sku", (row) => row.product?.metadata?.sku ?? String(row.product_id), {
        headerKey: "column.sku",
        priority: 1,
      }),
      c.text("name", (row) => row.product?.description_en?.product_name ?? "", {
        headerKey: "column.name",
        priority: 2,
      }),
      c.text("type", (row) => row.product?.type ?? "", {
        headerKey: "column.type",
        filter: "multiselect",
        filterOptions: PRODUCT_TYPES.map((entry) => ({ value: entry, label: entry })),
        priority: 4,
      }),
      c.custom(
        "status",
        (row) => row.status,
        (row) => (
          <Badge variant={APD_VARIANT[row.status]} size="sm">
            {row.status}
          </Badge>
        ),
        { headerKey: "column.status", filter: "select", priority: 3 },
      ),
      c.text("reason", (row) => row.reason ?? "", { headerKey: "apd.reason", priority: 3 }),
      c.text("flagged_at", (row) => day(row.flagged_at), { headerKey: "apd.flagged" }),
      c.text("discontinue_at", (row) => day(row.discontinue_at), { headerKey: "apd.discontinue-at" }),
      c.text("discontinued_at", (row) => day(row.discontinued_at), {
        headerKey: "apd.discontinued-at",
        defaultVisible: false,
      }),
      // The velocity figures the flagging job actually decided on — without
      // them the reason column is an assertion the operator can't check.
      c.number("weekly_4", (row) => trim2(row.product?.kpi_sales?.four_weeks_units_exp_avg), {
        headerKey: "column.sales.weekly-4",
        filter: "range",
        priority: 4,
      }),
      c.number("weekly_12", (row) => trim2(row.product?.kpi_sales?.twelve_weeks_units_exp_avg), {
        headerKey: "column.sales.weekly-12",
        filter: "range",
        priority: 4,
      }),
      c.number(
        "weekly_24",
        (row) => trim2(row.product?.kpi_sales?.twenty_four_weeks_units_exp_avg),
        { headerKey: "column.sales.weekly-24", filter: "range", priority: 5 },
      ),
      c.number("sales_52", (row) => units(row.product?.kpi_sales, "fifty_two_weeks"), {
        headerKey: "column.sales.52-weeks",
        filter: "range",
        priority: 5,
      }),
      c.text("note", (row) => row.note ?? "", { headerKey: "apd.note", defaultVisible: false }),
    ];
  }, []);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.config-automatic-discontinuation", "Automatic discontinuation")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t("navigation.item.config-automatic-discontinuation", "Automatic discontinuation")}
        subtitle={t("apd.subtitle", "Slow movers, and when they go.")}
        actions={
          <div className="flex flex-col items-end gap-0.5">
            <Switch
              checked={enabled}
              disabled={settingsQuery.isLoading || setEnabled.isPending}
              onChange={(on) => setEnabled.mutate(on)}
              label={t("apd.enabled", "Weekly job")}
            />
            <span className="text-[11px] text-ink-muted">
              {enabled
                ? t("apd.enabled.hint.on", "Runs Mondays at 14:00; flags, counts down, discontinues, emails.")
                : t("apd.enabled.hint.off", "Paused. Countdown dates stay as they are; the manual send still works.")}
            </span>
          </div>
        }
      />
      <DataTable
        data={data ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={isLoading}
        selectable
        onSelectionChange={setSelected}
        actions={[
          {
            label: t("apd.hold", "Put on hold"),
            icon: <PauseCircle className="size-4" />,
            write: true,
            requiresSelection: true,
            onClick: () => void setStatus("ON_HOLD", t("apd.hold.title", "Put the selection on hold?")),
          },
          {
            label: t("apd.release", "Remove hold"),
            icon: <PlayCircle className="size-4" />,
            write: true,
            requiresSelection: true,
            onClick: () =>
              void setStatus(
                "PENDING_DISCONTINUATION",
                t("apd.release.title", "Return the selection to pending discontinuation?"),
              ),
          },
        ]}
        emptyMessage={t("apd.empty", "Nothing is flagged for discontinuation.")}
      />
    </>
  );
}

function day(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : "";
}

/** Two decimals, truncated — the exponential averages are noisy past that. */
function trim2(value: number | null | undefined): number | null {
  return value === null || value === undefined ? null : Math.trunc(value * 100) / 100;
}
